import type { JSX } from 'react';
import { toast } from '@/lib/toast';
import { FormProvider, type FieldValues } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SiteInput } from '@/data/repos/sitesRepo';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';
import { useUnifiedForm } from '../../../../hooks/useUnifiedForm';
import {
  siteFieldOrder,
  siteFormFields,
  siteFormSchema,
  siteFormSections,
  type SiteFormValues,
} from '../../components/siteFormSchema';
import {
  EntityDialog,
  EntityDialogFooter,
  EntityFormFields,
  FormErrorBanner,
  FormErrorSummary,
  useEntityDialogForm,
  useEntityDialogSubmit,
} from '@/components/blocks/forms';
import { Button, DialogClose } from '@/components/ui';
import { scrollToFirstError } from '@/components/blocks/forms/formErrors';

interface NewSiteModalProps {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (siteId: string) => void;
}

export function NewSiteModal({
  companyId,
  open,
  onOpenChange,
  onCreated,
}: NewSiteModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const { sites } = useDataProvider();
  const isDev = import.meta.env?.DEV;

  const { fields } = useUnifiedForm({
    schema: siteFormSchema,
    fields: siteFormFields,
    fieldOrder: siteFieldOrder,
    fieldOverrides: {
      notes: { colSpan: 2 },
    },
  });

  const createSite = useMutation({
    mutationFn: async (values: SiteFormValues) => {
      const payload: SiteInput = {
        companyId,
        name: values.name,
        sedeOperativaAddress: values.sedeOperativaAddress,
        sedeOperativaCap: values.sedeOperativaCap,
        sedeOperativaCity: values.sedeOperativaCity,
        sedeOperativaProvince: values.sedeOperativaProvince,
        sedeOperativa: undefined,
        periodicity: values.periodicity as SiteInput['periodicity'],
        contractActivationDate: values.contractActivationDate,
        notes: values.notes,
        // CPI fields
        cpiPresent: values.cpiPresent,
        cpiProtocolNumber: values.cpiProtocolNumber,
        cpiLastRenewalDate: values.cpiLastRenewalDate,
        cpiExpiryDate: values.cpiExpiryDate,
        ammoniaActivityCodes: values.ammoniaActivityCodes,
        // Authorization fields
        authorizationProtocolNumber: values.authorizationProtocolNumber,
        authorizationIssueDate: values.authorizationIssueDate,
        authorizationExpiryDate: values.authorizationExpiryDate,
        authorizedNh3Kg: values.authorizedNh3Kg,
        officialGazettePublished: values.officialGazettePublished,
        officialGazetteDate: values.officialGazetteDate,
      };

      return sites.saveSite(payload);
    },
    onSuccess: async (siteId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.detail(companyId),
        }),
      ]);
      toast.success('Stabilimento creato con successo');
      onOpenChange(false);
      onCreated?.(siteId);
    },
    onError: (error) => {
      if (isDev) console.error('[NewSiteModal] onError', error);
    },
  });

  const { form, formId, errorCount, rootError, handleInvalidSubmit, setRootError } =
    useEntityDialogForm({
      open,
      schema: siteFormSchema,
      fields,
      data: null,
    });

  const handleSubmit = useEntityDialogSubmit({
    form,
    onSubmit: async (values: FieldValues) => {
      await createSite.mutateAsync(values as SiteFormValues);
    },
    setRootError,
  });

  const isSaving = createSite.isPending;
  const isDirty = form.formState.isDirty;

  const sections = siteFormSections;

  return (
    <EntityDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Nuovo stabilimento"
      size="md"
      isDirty={isDirty}
      isSaving={isSaving}
      formId={formId}
      banner={rootError ? <FormErrorBanner message={rootError} /> : null}
      summary={
        errorCount > 0 ? (
          <FormErrorSummary
            count={errorCount}
            onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
          />
        ) : null
      }
      footer={
        <EntityDialogFooter
          secondary={
            <DialogClose asChild>
              <Button variant="ghost" disabled={isSaving}>
                Chiudi
              </Button>
            </DialogClose>
          }
          primary={
            <Button type="submit" form={formId} disabled={!isDirty || isSaving}>
              {isSaving ? 'Salvataggio…' : 'Crea stabilimento'}
            </Button>
          }
        />
      }
    >
      <FormProvider {...form}>
        <form
          id={formId}
          onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
          noValidate
          className="space-y-4"
        >
          <EntityFormFields fields={fields} sections={sections} disabled={isSaving} />
        </form>
      </FormProvider>
    </EntityDialog>
  );
}
